import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Paper } from "@mui/material";
import SectionComparison from "./SectionComparison";
import { BORDER, BRAND, BRAND_SOFT, SEVERITIES, TEXT_PRIMARY, TEXT_SECONDARY } from "../constants";

const sectionKey = (section) => `${section.previousId}-${section.updatedId}`;

export const SeverityDots = ({ items }) => (
  <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexShrink: 0 }}>
    {SEVERITIES.map(({ key, color }) => {
      const count = items.filter((item) => item.severity === key).length;
      if (!count) return null;
      return (
        <Box key={key} component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, fontSize: 13, fontWeight: 700, color }}>
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color }} />
          {count}
        </Box>
      );
    })}
  </Box>
);

/** A vertical side menu next to a large view of the selected item */
export const SideMenuLayout = ({ title, items, selectedKey, onSelect, children }) => (
  <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: "flex-start" }}>
    <Paper
      elevation={0}
      component="nav"
      aria-label={title}
      sx={{
        width: { xs: "100%", md: 260 },
        flexShrink: 0,
        p: 0.75,
        borderRadius: 2,
        border: `1px solid ${BORDER}`,
        position: { md: "sticky" },
        top: { md: 88 },
        display: "flex",
        flexDirection: { xs: "row", md: "column" },
        gap: 0.25,
        overflowX: { xs: "auto", md: "visible" },
      }}
    >
      <Typography sx={{ display: { xs: "none", md: "block" }, px: 1.5, pt: 0.75, pb: 0.5, fontSize: 13, fontWeight: 700, color: TEXT_SECONDARY }}>
        {title}
      </Typography>
      {items.map((item) => {
        const selected = item.key === selectedKey;
        return (
          <Box
            key={item.key}
            component="button"
            type="button"
            onClick={() => onSelect(item.key)}
            aria-current={selected ? "true" : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: { xs: "auto", md: "100%" },
              flexShrink: 0,
              px: 1.5,
              py: 1.1,
              border: "none",
              borderRadius: 2,
              borderInlineStart: `3px solid ${selected ? BRAND : "transparent"}`,
              bgcolor: selected ? BRAND_SOFT : "transparent",
              font: "inherit",
              textAlign: "start",
              cursor: "pointer",
              transition: "background-color .15s",
              "&:hover": { bgcolor: selected ? BRAND_SOFT : "#f8fafc" },
              "&:focus-visible": { outline: `2px solid ${BRAND}`, outlineOffset: -2 },
            }}
          >
            <Typography
              noWrap
              sx={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: selected ? 700 : 600, color: selected ? TEXT_PRIMARY : "#334155" }}
            >
              {item.label}
            </Typography>
            {item.badge}
          </Box>
        );
      })}
    </Paper>
    <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>{children}</Box>
  </Box>
);

/**
 * Side-by-side comparison: the sections in a vertical side menu and the
 * selected section shown large. A jump from a correction selects the
 * section that holds the change and scrolls to it.
 */
const ComparisonExplorer = ({ sections, findingsById, visibleSeverities, focusedFindingIds, layout = "menu" }) => {
  const findingsOf = useMemo(() => {
    const map = new Map();
    sections.forEach((section) => {
      map.set(
        sectionKey(section),
        section.findingIds.map((id) => findingsById.get(id)).filter((f) => f && visibleSeverities.has(f.severity)),
      );
    });
    return map;
  }, [sections, findingsById, visibleSeverities]);

  const [selectedKey, setSelectedKey] = useState(null);
  const selected = sections.find((s) => sectionKey(s) === selectedKey) || sections[0];

  useEffect(() => {
    if (!focusedFindingIds) return undefined;
    const [firstId] = [...focusedFindingIds];
    const target = sections.find((s) => s.findingIds.includes(firstId));
    if (target) setSelectedKey(sectionKey(target));
    const timer = setTimeout(
      () => document.querySelector(`[data-finding="${firstId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }),
      150,
    );
    return () => clearTimeout(timer);
  }, [focusedFindingIds, sections]);

  if (!selected) return null;

  // Every section one under the other, without the side menu
  if (layout === "all") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {sections.map((section) => (
          <SectionComparison
            key={sectionKey(section)}
            section={section}
            findingsById={findingsById}
            visibleSeverities={visibleSeverities}
            focusedFindingIds={focusedFindingIds}
          />
        ))}
      </Box>
    );
  }

  return (
    <SideMenuLayout
      title={`${sections.length} סעיפים`}
      items={sections.map((section) => {
        const findings = findingsOf.get(sectionKey(section)) || [];
        return {
          key: sectionKey(section),
          label: section.label,
          badge: findings.length ? (
            <SeverityDots items={findings} />
          ) : (
            <Typography sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }}>ללא שינויים</Typography>
          ),
        };
      })}
      selectedKey={sectionKey(selected)}
      onSelect={setSelectedKey}
    >
      <SectionComparison
        key={sectionKey(selected)}
        section={selected}
        findingsById={findingsById}
        visibleSeverities={visibleSeverities}
        focusedFindingIds={focusedFindingIds}
      />
    </SideMenuLayout>
  );
};

export default ComparisonExplorer;
